import axiosInstance from './axiosCreator';
import type { Block, StudySession } from '../types/Study';
import { todaySessionReceived } from '../slices/todaySession/todaySessionSlice';
import { store } from '../store/store';
import { setVignettesForBlock } from '../slices/vignettes/vignettesSlice';
import { blocksLoaded } from '../slices/blocks/blocksSlice';
import {
  selectShouldFetchTopics,
  topicsReceived,
  topicsLoading,
  topicsFailed,
  markTopicsStale,
} from '../slices/todayTopics/todayTopicsSlice';
import {
  videosFailed,
  videosLoaded,
  videosLoading,
} from '../slices/videos/videosSlice';
import type { Video } from '../types/Video';
import type { FetchStudyTopicsForDateResponse } from '../types/StudyTopics';
import { normalizeQbankBlockResponse } from './qbank';
import { isClientUpdateRequiredError } from './clientUpdateRequirement';

const isStudyPerfDebugEnabled =
  import.meta.env.DEV &&
  ['1', 'true', 'yes'].includes(
    String(import.meta.env.VITE_STUDY_PERF_DEBUG ?? '').toLowerCase()
  );

interface ReportContentRequest {
  email: string;
  contentType: 'flashcard' | 'vignette';
  contentId: string;
  variantId: string;
  contentText: string;
  reason: string;
}

export interface QbankAvailabilityWarning {
  code: 'QBANK_WORKLOAD_UNDERFILLED';
  requestedCount: number;
  clampedRequestedCount: number;
  actualCount: number;
  currentWorkloadTier: string;
  currentWorkloadLabel: string;
  isAtHighestWorkload: boolean;
  nextWorkloadTier: string | null;
  nextWorkloadLabel: string | null;
}

export interface ModifyStudySessionResponse {
  studySession: StudySession;
  qbankAvailabilityWarning: QbankAvailabilityWarning | null;
}

interface GuardedCommitOptions<T> {
  shouldAccept?: (value: T) => boolean;
}

export function fetchTodayStudySession(reset?: boolean): Promise<StudySession>;
export function fetchTodayStudySession(
  reset: boolean | undefined,
  options: GuardedCommitOptions<StudySession>
): Promise<StudySession | undefined>;
export async function fetchTodayStudySession(
  reset?: boolean,
  options?: GuardedCommitOptions<StudySession>
) {
  const startedAt = performance.now();
  try {
    const response = await axiosInstance.get('/api/v5/study/session', {
      params: {
        reset,
      },
    });

    if (isStudyPerfDebugEnabled) {
      console.info('today-session-perf', {
        event: 'fetchTodayStudySession',
        durationMs: Math.round(performance.now() - startedAt),
        reset: reset ?? false,
        studySessionId: response.data?.data?.id ?? null,
      });
    }
    const studySession: StudySession = response.data.data;
    if (options?.shouldAccept && !options.shouldAccept(studySession)) {
      return undefined;
    }

    store.dispatch(todaySessionReceived(studySession));
    return studySession;
  } catch (error) {
    if (isStudyPerfDebugEnabled) {
      console.info('today-session-perf', {
        event: 'fetchTodayStudySession_failed',
        durationMs: Math.round(performance.now() - startedAt),
        reset: reset ?? false,
      });
    }
    console.error(error);
    throw error;
  }
}

export const fetchStudySessionByIdForReview = async (
  sessionId: string,
  blockId: string
) => {
  try {
    const response = await axiosInstance.get(`/study/session/${sessionId}`);

    const studySession = {
      ...response.data.data,
      isReview: true,
      activeBlockId: blockId,
    };

    store.dispatch(todaySessionReceived(studySession));
    return studySession;
  } catch (error) {
    console.error(error);
    if (isClientUpdateRequiredError(error)) {
      throw error;
    }
  }
};

export const fetchBlocksByStudySessionId = async (
  _sessionId: string,
  options?: GuardedCommitOptions<Block[]>
) => {
  try {
    const response = await axiosInstance.get('/api/v5/study/blocks');
    if (options?.shouldAccept && !options.shouldAccept(response.data.blocks)) {
      return undefined;
    }

    store.dispatch(blocksLoaded(response.data.blocks));
    return response.data;
  } catch (error) {
    console.error(error);
    if (isClientUpdateRequiredError(error)) {
      throw error;
    }
  }
};

export const fetchVignettesForBlock = async (
  blockId: string,
  options?: GuardedCommitOptions<unknown[]>
): Promise<
  ReturnType<typeof normalizeQbankBlockResponse> | { vignettes: unknown[] }
> => {
  try {
    const response = await axiosInstance.get(`/api/v5/qbank/blocks/${blockId}`);
    const normalized = normalizeQbankBlockResponse(response.data);
    const vignettes = normalized?.vignettes ?? [];

    if (options?.shouldAccept && !options.shouldAccept(vignettes)) {
      return null;
    }

    store.dispatch(
      setVignettesForBlock({
        blockId,
        vignettes,
      })
    );

    return normalized ?? { vignettes };
  } catch (error) {
    console.error(error);
    if (isClientUpdateRequiredError(error)) {
      throw error;
    }
    return null;
  }
};

/**
 * Fetch videos tied to a specific study session (NBME/COMLEX)
 * Stores videos under videos.byLectureId using the sessionId as the key.
 */
export const fetchVideosForStudySession = async (
  sessionId: string,
  options?: GuardedCommitOptions<Video[]>
): Promise<Video[]> => {
  try {
    store.dispatch(videosLoading(sessionId));

    // Align with existing study endpoints (e.g., blocks, flashcards)
    const response = await axiosInstance.get(
      `/api/v5/study/session/${sessionId}/videos`
    );

    const videos: Video[] =
      response.data?.data?.videos ?? response.data?.videos ?? [];

    if (options?.shouldAccept && !options.shouldAccept(videos)) {
      return [];
    }

    store.dispatch(videosLoaded({ lectureId: sessionId, videos }));
    return videos;
  } catch (error) {
    if (!options?.shouldAccept || options.shouldAccept([])) {
      store.dispatch(videosFailed(sessionId));
    }
    console.error('Error fetching videos for study session:', error);
    throw error;
  }
};

export const CONTENT_REPORT_THANKS = 'Thank you, we will review this shortly!';

export const reportContent = async (reportInfo: ReportContentRequest) => {
  try {
    const response = await axiosInstance.post(
      '/study/content/report',
      reportInfo
    );

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const resetExamScheduling = async (params: {
  examId: string;
  contentType: 'flashcards' | 'qbank' | 'videos';
}) => {
  try {
    const response = await axiosInstance.post(
      '/api/v5/study/scheduling/reset',
      {
        examId: params.examId,
        contentType: params.contentType,
      }
    );

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const modifyStudySession = async (
  studySessionId: string,
  flashcardCount: number,
  vignetteCount: number,
  videoCount: number
): Promise<ModifyStudySessionResponse> => {
  try {
    // Intended for NBME/COMLEX Today Session customization only.
    const response = await axiosInstance.post(`/api/v5/study/modify-session`, {
      studySessionId,
      flashcardCount,
      vignetteCount,
      videoCount,
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface FetchTodayTopicsIfNeededParams {
  date: string;
  examId: string;
  sessionId: string;
}

export const fetchTodayTopicsIfNeeded = async ({
  date,
  examId,
  sessionId,
}: FetchTodayTopicsIfNeededParams) => {
  const state = store.getState();

  const shouldFetch = selectShouldFetchTopics({ date, examId, sessionId })(
    state
  );

  if (!shouldFetch) {
    return;
  }

  store.dispatch(topicsLoading());

  try {
    const response = await fetchStudyTopicsForDate(date);
    const latestState = store.getState();
    const latestSession = latestState.todaySession.session;

    // Drop stale in-flight responses from prior exam/session context.
    if (
      !latestSession?.id ||
      latestSession.id !== sessionId ||
      latestSession.examId !== examId
    ) {
      store.dispatch(markTopicsStale());
      return;
    }

    if (response?.topics) {
      store.dispatch(
        topicsReceived({
          topics: response.topics,
          date,
          examId,
          sessionId,
        })
      );
    } else {
      store.dispatch(topicsReceived({ topics: [], date, examId, sessionId }));
    }
  } catch (error) {
    console.error('Error fetching topics for date:', date, error);

    store.dispatch(
      topicsFailed(error instanceof Error ? error.message : 'Unknown error')
    );
  }
};

export const fetchStudyTopicsForDate = async (date: string) => {
  try {
    const response = await axiosInstance.get<FetchStudyTopicsForDateResponse>(
      `/study/topics?date=${date}`
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};
